import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { auditTime, distinctUntilChanged, filter, switchMap, tap } from 'rxjs/operators';
import { DTRUM_ACTION_NAME, DynatraceHelperService } from '@shared/services/dynatraceHelper.service';
import { Router } from '@angular/router';
import { CountryService } from '@shared/services/country.service';
import { GoogleMapsAddressService } from '@shared/services/google-maps-address.service';
import { AmplitudeService } from '@shared/services/amplitude.service';
import { NGXLogger } from 'ngx-logger';

@Component({
    selector: 'google-places-search-field',
    templateUrl: './google-places-search-field.component.html'
})
export class GooglePlacesSearchFieldComponent implements OnInit {

    @Input()
    country: string;

    @Input()
    maxSuggestions: number = 5;

    @Input()
    searchControlLabel: string;

    @Input()
    searchControlPlaceholder: string;

    @Output()
    resultSelected = new EventEmitter<google.maps.places.PlaceResult>();

    searchControl: FormControl<string>;
    addressSearchLoading: boolean = false;
    addressSearchTypeahead = new Subject<string>();
    addressSearchResults: google.maps.places.AutocompletePrediction[] = [];
    noActionableResult: boolean = false;
    addressSearchActionId;

    constructor(
        private readonly countryService: CountryService,
        private readonly googleMapsService: GoogleMapsAddressService,
        private readonly router: Router,
        protected logger: NGXLogger,
        private readonly amplitudeService: AmplitudeService,
        private readonly dynatrace: DynatraceHelperService
    ) {
        this.searchControl = new FormControl<string>(null);
    }


    ngOnInit(): void {
        let addressSearchActionId: number;

        this.addressSearchTypeahead.pipe(
            auditTime(300),
            distinctUntilChanged(),
            tap(() => {
                this.addressSearchResults = [];
            }),
            filter(term => term?.length > 2),
            tap(() => {
                this.addressSearchLoading = true;
                this.noActionableResult = false;
                addressSearchActionId = this.dynatrace.enterAction(DTRUM_ACTION_NAME.ADDRESS_SEARCH_GOOGLE);
            }),
            switchMap(async term => {
                const countryIso2 = await this.iso3ToIso2(this.country);

                return this.googleMapsService.loadPredictions(countryIso2, term).then((predictions) => {
                    this.addressSearchLoading = false;
                    this.noActionableResult = predictions.length > 0 && !predictions.some(p => this.isFullAddress(p));

                    this.amplitudeService.trackAddressSearchPerformed(
                        this.router.url,
                        this.country,
                        predictions.length,
                        this.noActionableResult
                    );

                    return predictions.map(p => {
                        return {
                            ...p,
                            disabled: !this.isFullAddress(p)
                        };
                    }).slice(0, this.maxSuggestions);

                }, () => {
                    this.addressSearchLoading = false;
                    this.amplitudeService.trackAddressSearchProviderFailed(this.router.url, this.country);
                    return [];
                })
            })
        ).subscribe(results => {
            this.addressSearchResults = results;
            this.dynatrace.leaveAction(addressSearchActionId);
        });

        this.searchControl.valueChanges.subscribe(async place_id => {
            this.logger.debug("Place Selected", place_id);
            if (!place_id) return;

            const googleMapsFetchPlaceId = this.dynatrace.enterAction(DTRUM_ACTION_NAME.ADDRESS_LOOKUP_GOOGLE);

            const place = await this.googleMapsService.fetchPlace(place_id);
            this.dynatrace.leaveAction(googleMapsFetchPlaceId);

            this.resultSelected.emit(place);
        });

    }

    private isFullAddress(prediction: google.maps.places.AutocompletePrediction): boolean {
        return prediction.types.some(t => t === "street_address" || t === "premise" || t === "subpremise");
    }

    private async iso3ToIso2(countryIso3: string): Promise<string> {
        const countries = await this.countryService.getCurrentCountries();
        return countries.find(country => country.iso3 === countryIso3)?.iso2.toLowerCase();
    }

}
